import { useSnackbarStack } from '@superdispatch/ui';
import { useCallback, useEffect } from 'react';
import { useMatch, useNavigate, useResolvedPath } from 'react-router-dom';
import { trackEvent } from 'shared/helpers/Analytics';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import { useACHPaymentInfo } from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { ACHBankDetailsRestrictedView } from './ach-payment-info/ACHBankDetailsRestrictedView';
import { ACHChangeBankDetailsDrawer } from './ach-payment-info/ACHChangeBankDetailsDrawer';
import { ACHFormView } from './ach-payment-info/ACHFormView';
import { ACHNoAccessView } from './ach-payment-info/ACHNoAccessView';
import { isACHDataEmpty } from './ach-payment-info/ACHPaymentInfoHelpers';
import { useACHPaymentInfoForm } from './ach-payment-info/useACHPaymentInfoForm';

export function ProfileACHPaymentInfoPage() {
  const { data: paymentInfo, error } = useACHPaymentInfo();
  const { addSnackbar } = useSnackbarStack();
  const [searchParams] = useQuery();
  const isACHPaymentInfoEmpty = isACHDataEmpty(paymentInfo);

  const shouldShowRestrictedView = !isACHPaymentInfoEmpty;

  const shouldShowNoAccessView =
    isACHPaymentInfoEmpty && paymentInfo?.hours_until_update_allowed;

  const changeBankDetailsPath = useResolvedPath('change-bank-details');
  const isCorrectPath = !!useMatch(changeBankDetailsPath.pathname);
  const navigate = useNavigate();

  const handleSaveSuccess = useCallback(() => {
    trackEvent('CTMS: Saved ACH Payment Info');

    addSnackbar('ACH payment info was successfully updated', {
      variant: 'success',
    });

    trackCarrierProfileEvent({
      name: 'Carrier Updated Carrier Info',
      utm_medium: 'Carrier Profile',
      utm_content: 'ACH Payment Info',
    });

    if (searchParams.back_url) {
      openExternalURL(decodeURIComponent(searchParams.back_url));
    }
  }, [addSnackbar, searchParams]);

  const { formik, updatedPaymentInfo, setUpdatedPaymentInfo } =
    useACHPaymentInfoForm({
      paymentInfo,
      onSuccess: () => {
        navigate(`${changeBankDetailsPath.pathname}?step=confirm`, {
          replace: true,
        });
      },
    });

  const isChangeBankDetailsDrawerOpen =
    isCorrectPath && (!isACHPaymentInfoEmpty || !!updatedPaymentInfo);

  useEffect(() => {
    if (error) {
      addSnackbar('Failed to fetch ACH payment info', { variant: 'error' });
    }
  }, [addSnackbar, error]);

  useEffect(() => {
    trackEvent('CTMS: Started Add ACH Payment Info', {
      utm_medium: searchParams.utm_medium || 'carrier_profile',
    });
  }, [searchParams]);

  return (
    <>
      <DocumentTitle title="ACH Payment Info - Profile" />

      {shouldShowNoAccessView ? (
        <ACHNoAccessView
          hoursUntilUpdateAllowed={paymentInfo?.hours_until_update_allowed ?? 0}
        />
      ) : (
        <PermissionView name="ach_payment_info" permission="can_view">
          <>
            <ACHChangeBankDetailsDrawer
              isOpen={isChangeBankDetailsDrawerOpen}
              updatedPaymentInfo={updatedPaymentInfo}
              setUpdatedPaymentInfo={setUpdatedPaymentInfo}
              onComplete={handleSaveSuccess}
            />
            {shouldShowRestrictedView && paymentInfo ? (
              <ACHBankDetailsRestrictedView paymentInfo={paymentInfo} />
            ) : (
              <ACHFormView formik={formik} />
            )}
          </>
        </PermissionView>
      )}
    </>
  );
}
